import * as zod from 'zod';
import type { CheckboxID, InputID } from '../types';
import { Errors, InputIDs, CheckboxIDs } from '../types';

/** Numbers only */
export const numbersOnly = /^\d*$/;

/** No special charaters and no numbers allowed */
export const noSpecCharsNoNumbers = /^[A-Za-z.\-ßÄÖÜäöü ]*$/;

/** No special charaters allowed */
export const noSpecChars = /^[A-Za-z0-9.\-ßÄÖÜäöü ]*$/;

const generalFormSchema = {
  [InputIDs.FIRSTNAME]: zod
    .string()
    .nonempty({ message: Errors.REQUIRED })
    .regex(noSpecCharsNoNumbers, Errors.NOSPECCHARSNONUMBERS),
  [InputIDs.LASTNAME]: zod
    .string()
    .nonempty({ message: Errors.REQUIRED })
    .regex(noSpecCharsNoNumbers, Errors.NOSPECCHARSNONUMBERS),
  [InputIDs.STREET]: zod
    .string()
    .nonempty({ message: Errors.REQUIRED })
    .regex(noSpecCharsNoNumbers, Errors.NOSPECCHARSNONUMBERS),
  [InputIDs.HOUSENUMBER]: zod
    .string()
    .nonempty({ message: Errors.REQUIRED })
    .regex(noSpecChars, Errors.NOSPECCHARS),
  [InputIDs.POSTALCODE]: zod
    .string()
    .nonempty({ message: Errors.REQUIRED })
    .regex(numbersOnly, Errors.NUMBERSONLY),
  [InputIDs.CITY]: zod
    .string()
    .nonempty({ message: Errors.REQUIRED })
    .regex(noSpecCharsNoNumbers, Errors.NOSPECCHARSNONUMBERS),
  [InputIDs.EMAIL]: zod.string().email(Errors.EMAIL).nonempty({ message: Errors.REQUIRED }),
  [InputIDs.MESSAGE]: zod.string().nonempty({ message: Errors.REQUIRED }),
  [InputIDs.PHONENUMBER]: zod.string().optional(),
  [InputIDs.CUSTOMERID]: zod.string().regex(noSpecChars, Errors.NOSPECCHARS).optional(),
  [InputIDs.SUBJECT]: zod.string().optional(),
};

const rateProtectionSchema = {
  ...generalFormSchema,
  [InputIDs.CUSTOMERID]: zod
    .string()
    .nonempty({ message: Errors.REQUIRED })
    .regex(noSpecChars, Errors.NOSPECCHARS),
  [InputIDs.MESSAGE]: zod.string(),
  [InputIDs.BIRTHDAY]: zod.preprocess(
    (arg) => (typeof arg === 'string' || arg instanceof Date ? new Date(arg) : undefined),
    zod.date({ invalid_type_error: Errors.INVALIDDATE }),
  ),
  [CheckboxIDs.CANCELADVERTISING]: zod.boolean(),
  [CheckboxIDs.CANCELMARKETING]: zod.boolean(),
  [CheckboxIDs.CANCELNL]: zod.boolean(),
  [CheckboxIDs.TERMS]: zod.literal(true, { errorMap: () => ({ message: Errors.REQUIRED }) }),
};

const newsletterSubscribeSchema = {
  [InputIDs.EMAIL]: zod.string().nonempty({ message: Errors.REQUIRED }).email(Errors.EMAIL),
};

const newsletterUnsubscribeSchema = {
  [InputIDs.EMAIL]: zod.string().nonempty({ message: Errors.REQUIRED }).email(Errors.EMAIL),
  [InputIDs.FREQUENCY]: zod.string().nonempty({ message: Errors.REQUIRED }),
  [InputIDs.PAUSE]: zod.string().optional(),
};

export const contactFormSchema = zod.object(generalFormSchema);
export const rateProtectionFormSchema = zod.object(rateProtectionSchema);

export const newsletterSubscribeFormSchema = zod.object(newsletterSubscribeSchema);
export const newsletterUnsubscribeFormSchema = zod.object(newsletterUnsubscribeSchema);

export type SchemaType =
  | zod.ZodObject<Record<InputID, zod.ZodTypeAny>>
  | zod.ZodObject<Record<CheckboxID, zod.ZodTypeAny>>
  | zod.ZodObject<Record<string, zod.ZodTypeAny>>;
